import React, { useState } from 'react';
import { Search, Sparkles, Loader2, X } from 'lucide-react';
import BASE_URL from './config';

/**
 * Natural-language ride search component.
 *
 * Sends free-text queries to /api/rides/search, which uses an LLM to
 * semantically match rides. Shows a short "understood" summary.
 */
function NLSearch({ onResults, onClear }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [understood, setUnderstood] = useState(null);
  const [resultCount, setResultCount] = useState(null);

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setUnderstood(null);
    setResultCount(null);

    try {
      const response = await fetch(`${BASE_URL}/api/rides/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: trimmed }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Search failed (${response.status})`);
      }

      const data = await response.json();
      setUnderstood(data.understood);
      setResultCount(data.total_matched);

      if (onResults) {
        onResults(data.results);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setUnderstood(null);
    setError(null);
    setResultCount(null);
    if (onClear) onClear();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const examples = [
    "Ride to JFK Friday afternoon",
    "Anyone going to the airport this weekend?",
    "Need a ride from LGA tomorrow",
  ];

  return (
    <div className="nl-search-container">
      {/* Search input row */}
      <div className="nl-search-input-row">
        <div className="nl-search-input-wrapper">
          <Search className="nl-search-icon w-4 h-4" />
          <input
            type="text"
            className="nl-search-input"
            placeholder="Describe the ride you're looking for..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!e.target.value.trim() && understood) {
                handleClear();
              }
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          {query && (
            <button className="nl-search-clear" onClick={handleClear} title="Clear search">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <button
          className="nl-search-btn"
          onClick={handleSearch}
          disabled={loading || !query.trim()}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 nl-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Quick example chips */}
      {!understood && !error && !loading && (
        <div className="nl-search-examples">
          {examples.map((ex, i) => (
            <button
              key={i}
              className="nl-search-chip"
              onClick={() => { setQuery(ex); }}
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="nl-search-error">
          {error}
        </div>
      )}

      {/* Understood summary */}
      {understood && (
        <div className="nl-search-intent">
          <div className="nl-search-intent-header">
            <span className="nl-search-intent-label">
              <Sparkles className="w-3 h-3" />
              {resultCount != null ? `${resultCount} ride${resultCount !== 1 ? 's' : ''} found` : 'Searching...'}
              {understood && <span className="nl-search-understood"> — {understood}</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default NLSearch;
